/**
 * A law firm's roles: Owner, Attorney, Paralegal, Front desk.
 * @file src/shared/firm-roles.ts
 *
 * The split most practice software uses — MyCase and PracticePanther have
 * Attorney, Paralegal and Staff under an Admin; Clio has an owner and
 * administrators over users; Lawmatics adds intake specialists — kept to four.
 *
 * Each role includes everything the one below it can do:
 *
 *   Front desk   prospects: the pipeline, notes, appointments, intake forms
 *   Paralegal    + case prep: documents, document requests, draft matters,
 *                  conflict checks
 *   Attorney     + hiring: agreements and countersigning, approving draft
 *                  matters, opening matters and sending them to the case system
 *   Owner        + the firm's set-up: forms, templates, stages, scoring,
 *                  automation, notifications, settings, integrations, users
 *
 * ── Stored as appkit's role.level, on its linear ladder ───────────────────
 * Front desk user.basic · Paralegal moderator.approve · Attorney
 * moderator.manage · Owner admin.tenant. Because appkit's hierarchy inherits
 * upward, a route gated `requireUserRoles(['moderator.approve'])` admits
 * paralegals, attorneys and owners and nobody below — which is exactly
 * "Paralegal and up". The routes use FIRM_ROLE_GATE's strings.
 *
 * @llm-rule WHEN: Gating a firm route or a firm screen by role, or naming a firm user's role
 * @llm-rule AVOID: A fifth role, or per-user permission toggles, without a real need
 */

export type FirmRoleId = 'front-desk' | 'paralegal' | 'attorney' | 'owner';

export interface FirmRole {
  id: FirmRoleId;
  label: string;
  role: string;
  level: string;
  /** One line a firm owner reads when choosing it. */
  summary: string;
}

/** Highest first, the order the Users page lists them in. */
export const FIRM_ROLES: FirmRole[] = [
  {
    id: 'owner',
    label: 'Owner',
    role: 'admin',
    level: 'tenant',
    summary: 'Everything, including the firm’s set-up: forms, templates, stages, scoring, automation, settings, integrations and users.',
  },
  {
    id: 'attorney',
    label: 'Attorney',
    role: 'moderator',
    level: 'manage',
    summary: 'Hires clients: sends and countersigns agreements, approves draft matters, opens matters and sends them to the case system.',
  },
  {
    id: 'paralegal',
    label: 'Paralegal',
    role: 'moderator',
    level: 'approve',
    summary: 'Prepares cases: documents, document requests, draft matters and conflict checks, on top of front-desk work.',
  },
  {
    id: 'front-desk',
    label: 'Front desk',
    role: 'user',
    level: 'basic',
    summary: 'Handles prospects: the pipeline, notes, appointments and sending intake forms.',
  },
];

/** The route gate for "this role and up", in appkit's dotted form. */
export const FIRM_ROLE_GATE: Record<FirmRoleId, string> = {
  'front-desk': 'user.basic',
  paralegal: 'moderator.approve',
  attorney: 'moderator.manage',
  owner: 'admin.tenant',
};

/* appkit's default ladder, low to high. */
const LADDER = [
  'user.basic', 'user.pro', 'user.max',
  'moderator.review', 'moderator.approve', 'moderator.manage',
  'admin.tenant', 'admin.org', 'admin.system',
];
const MIN_RANK: Record<FirmRoleId, number> = { 'front-desk': 0, paralegal: 4, attorney: 5, owner: 6 };

const rankOf = (u: { role?: string | null; level?: string | null } | null | undefined) =>
  LADDER.indexOf(`${u?.role ?? ''}.${u?.level ?? ''}`);

/** Whether this account has at least `min`. Any firm sign-in is front desk. */
export function hasFirmRole(u: { role?: string | null; level?: string | null } | null | undefined, min: FirmRoleId): boolean {
  return min === 'front-desk' ? Boolean(u) : rankOf(u) >= MIN_RANK[min];
}

/** The role an account holds. A legacy pair reads as the role it can act as. */
export function firmRoleOf(u: { role?: string | null; level?: string | null } | null | undefined): FirmRole {
  const id: FirmRoleId = hasFirmRole(u, 'owner')
    ? 'owner'
    : hasFirmRole(u, 'attorney')
      ? 'attorney'
      : hasFirmRole(u, 'paralegal')
        ? 'paralegal'
        : 'front-desk';
  return FIRM_ROLES.find((r) => r.id === id)!;
}
